import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Target, Users, TrendingUp, Award, Shield, Heart, Zap, CheckCircle2, Sparkles, Mountain } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function About() {
  const values = [
    {
      icon: Shield,
      title: 'Integrity',
      description: 'We uphold integrity with honesty, fostering trust, respect, and meaningful relationships',
      color: 'blue-600'
    },
    {
      icon: Sparkles,
      title: 'Optimism',
      description: 'We face challenges with optimism, driving creativity, innovation, and resilient growth',
      color: 'orange-600'
    },
    {
      icon: CheckCircle2,
      title: 'Accountability',
      description: 'We honor our commitments, own our decisions, and maintain full transparency',
      color: 'green-600'
    },
    {
      icon: Mountain,
      title: 'Perseverance',
      description: 'We tackle challenges with persistence and resilience, ensuring meaningful outcomes',
      color: 'teal-600'
    }
  ];

  // Team data moved to Leadership page
  
  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section with Image (subtle difference) */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1576267423048-15c0040fec78?w=1600" 
            alt="Team collaboration" 
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900/95 to-teal-800/90"></div>
        </div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <Sparkles className="mx-auto mb-4 text-blue-200 animate-pulse" size={48} />
          <h1 
            className="text-5xl sm:text-6xl font-bold text-white mb-6" 
            data-testid="about-hero-title"
            data-anim="typing"
            data-speed="35"
          >
            About Symetrix
          </h1>
          <p 
            className="text-xl text-blue-100"
            data-anim="fade-up"
            data-delay="120"
          >
            Transforming the talent landscape with <span
              data-anim="rotating-text"
              data-words="strategic precision|human insight|innovative solutions|unwavering commitment|exceptional results"
              data-speed="2000"
              data-transition="500"
              className="font-semibold text-transparent text-2xl bg-clip-text bg-[linear-gradient(90deg,#fbbf24,#f59e0b,#fcd34d)] bg-[length:200%_100%] animate-[gradient-move_3s_linear_infinite]"
            >strategic precision</span> and excellence.
          </p>
        </div>
      </section>
      
      {/* Our Story */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 
                className="text-4xl font-bold text-gray-900 mb-6"
                data-anim="fade-up"
              >
                Our Story
              </h2>
              <p 
                className="text-lg text-gray-600 mb-4"
                data-anim="fade-up"
                data-delay="100"
              >
                Founded in 2011, Symetrix HR Consulting emerged from a simple yet powerful vision: to revolutionize how organizations connect with exceptional talent.
              </p>
              <p 
                className="text-lg text-gray-600 mb-4"
                data-anim="fade-up"
                data-delay="200"
              >
                Over the past 15 years, we've grown from a boutique consultancy to a trusted partner for hundreds of leading organizations across diverse industries.
              </p>
              <p 
                className="text-lg text-gray-600"
                data-anim="fade-up"
                data-delay="300"
              >
                What sets us apart is our commitment to understanding both sides of the equation — the unique needs of every client and the aspirations of every candidate.
              </p>
            </div>
            <div 
              data-anim="fade-up"
              data-delay="150"
            >
              <img 
                src="https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=800" 
                alt="Team meeting" 
                className="rounded-lg shadow-xl"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>
      
      {/* Our Values */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 
              className="text-4xl font-bold text-gray-900 mb-4"
              data-anim="fade-up"
            >
              Our Values
            </h2>
            <p 
              className="text-lg text-gray-600 max-w-3xl mx-auto"
              data-anim="fade-up"
              data-delay="100"
            >
              The principles that guide everything we do and every relationship we build.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-anim="list-stagger" data-stagger="120">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <Card 
                  key={index} 
                  className="hover:shadow-xl hover:border-blue-500/50 transition-all border-none duration-300"
                >
                  <CardContent className="p-6 text-center">
                    <div 
                      className={`w-16 h-16 bg-${value.color} rounded-lg flex items-center justify-center mx-auto mb-4`}
                    >
                      <IconComponent className="text-white" size={32} />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">
                      {value.title}
                    </h3>
                    <p className="text-gray-600">
                      {value.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>
      
      {/* Team section removed - now in Leadership page */}
      
      {/* Stats Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 
              className="text-4xl font-bold text-gray-900 mb-4"
              data-anim="fade-up"
            >
              Our Impact in Numbers
            </h2>
            <p 
              className="text-lg text-gray-600 max-w-3xl mx-auto"
              data-anim="fade-up"
              data-delay="100"
            >
              A track record of excellence, proven results, and lasting partnerships.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8" data-anim="list-stagger" data-stagger="100">
            <Card className="text-center border-none shadow-md hover:shadow-lg transition-all">
              <CardContent className="p-8">
                <div 
                  className="text-4xl font-bold text-blue-600 mb-2"
                  data-anim="counter"
                  data-from="0"
                  data-to="14"
                  data-duration="1500"
                >14+</div>
                <p 
                  className="text-gray-600 font-medium"
                  data-anim="fade-up"
                  data-delay="200"
                >
                  Years of Excellence
                </p>
              </CardContent>
            </Card>
            <Card className="text-center border-none shadow-md hover:shadow-lg transition-all">
              <CardContent className="p-8">
                <div 
                  className="text-4xl font-bold text-green-600 mb-2"
                  data-anim="counter"
                  data-from="0"
                  data-to="15"
                  data-duration="1500"
                >15+</div>
                <p 
                  className="text-gray-600 font-medium"
                  data-anim="fade-up"
                  data-delay="200"
                >
                  Clients Served
                </p>
              </CardContent>
            </Card>
            <Card className="text-center border-none shadow-md hover:shadow-lg transition-all">
              <CardContent className="p-8">
                <div 
                  className="text-4xl font-bold text-teal-600 mb-2"
                  data-anim="counter"
                  data-from="0"
                  data-to="1628"
                  data-duration="2000"
                >1,628</div>
                <p 
                  className="text-gray-600 font-medium"
                  data-anim="fade-up"
                  data-delay="200"
                >
                  Strategic Placements
                </p>
              </CardContent>
            </Card>
            <Card className="text-center border-none shadow-md hover:shadow-lg transition-all">
              <CardContent className="p-8">
                <div 
                  className="text-4xl font-bold text-orange-600 mb-2"
                  data-anim="percent"
                  data-from="0"
                  data-to="97.4"
                  data-duration="2000"
                >97.4%</div>
                <p 
                  className="text-gray-600 font-medium"
                  data-anim="fade-up"
                  data-delay="200"
                >
                  Client Satisfaction
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-blue-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 
            className="text-4xl font-bold mb-4"
            data-anim="fade-up"
          >
            Ready to Partner with Excellence?
          </h2>
          <p 
            className="text-xl mb-8 text-blue-100"
            data-anim="fade-up"
            data-delay="100"
          >
            Let's discuss how Symetrix can help you achieve your talent goals.
          </p>
          <Link to="/contact">
            <Button 
              size="lg" 
              className="bg-white text-blue-600 hover:bg-blue-50 px-8 transform hover:scale-105 transition-all shadow-xl"
              data-anim="scale-in"
              data-delay="200"
            >
              Get in Touch
            </Button>
          </Link>
        </div>
      </section>
      
      <Footer />
    </div>
  );
}
